import { useState, type FormEvent, type ReactElement } from 'react';

import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { PasswordRecoveryBox } from '@/features/auth/PasswordRecoveryBox';
import { ProfileFields, toDraft, toInput } from '@/features/auth/ProfileFields';
import { MemberPhotoField } from '@/features/members/MemberPhotoField';
import {
  deleteStaffPhoto,
  issueStaffTemporaryPassword,
  sendStaffPasswordResetEmail,
  updateStaffProfile,
  uploadStaffPhoto,
  type Staff,
} from '@/features/staff/api';
import { ApiError } from '@/lib/api/http';

/**
 * 理事・講師／サクセス担当のプロフィールを事務局が代理編集する（D-081）。
 * 本人は「アカウント設定」から同じ内容を編集できる。
 */
export function StaffProfileEditor({
  staff,
  onSaved,
  onClose,
}: {
  staff: Staff;
  onSaved: (updated: Staff) => void;
  onClose: () => void;
}): ReactElement {
  const [draft, setDraft] = useState(() => toDraft(staff));
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      onSaved(await updateStaffProfile(staff.user_id, toInput(draft)));
      setNotice('プロフィールを保存しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'プロフィールを保存できませんでした。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-4">
      <MetaBox title={`プロフィールの編集: ${staff.display_name ?? staff.email}`}>
        {notice && (
          <Notice kind="success" onDismiss={() => setNotice(null)}>
            {notice}
          </Notice>
        )}
        {error && (
          <Notice kind="error" dismissible={false}>
            {error}
          </Notice>
        )}

        <p className="mb-3">
          <span className="text-wptext-muted">ログインID（メールアドレス）: </span>
          <span className="text-wptext-strong">{staff.email}</span>
        </p>

        <MemberPhotoField
          photoUrl={staff.photo_url}
          name={staff.display_name ?? staff.email}
          editable
          onUpload={async (file) => onSaved(await uploadStaffPhoto(staff.user_id, file))}
          onDelete={async () => onSaved(await deleteStaffPhoto(staff.user_id))}
        />

        <form className="mt-4" onSubmit={(event) => void onSubmit(event)}>
          <ProfileFields draft={draft} onChange={setDraft} idPrefix="staff" />

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy}
              className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
            >
              {busy ? '保存中…' : '保存する'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              閉じる
            </button>
          </div>
          <p className="mt-2 text-xs text-wptext-muted">
            本人も「アカウント設定」から同じ内容を編集できます。変更は監査ログに残ります。
          </p>
        </form>

        <div className="mt-6 border-t border-wpborder pt-4">
          <h3 className="mb-1 font-semibold text-wptext-strong">パスワードを紛失したとき</h3>
          <PasswordRecoveryBox
            sendTo={staff.contact_email ?? staff.email}
            onSendResetEmail={() => sendStaffPasswordResetEmail(staff.user_id)}
            onIssueTemporary={() => issueStaffTemporaryPassword(staff.user_id)}
          />
        </div>
      </MetaBox>
    </div>
  );
}
